// src/pages/chat/settings/settings-fullscreen-modal.tsx
import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { ProfileSettings } from './profile/profile';
import { NotificationsSettings } from './notification/notification';
import { AccountSettings } from './account/account-settings';
import { PrivacySettings } from './privacy/privacy';
import { BillingSettings } from './billing/billing-settings';
import ChangePasswordPanel from './account/change-password/change-password';
import { ComparePlansPanel } from './billing/compare-plans/compare-plans';
import { useSettingsModal, SettingsTab } from './settings.hooks';
import {
  H1,
  NavTitle,
  Header as SheetHeader,
  Overlay,
  Sheet,
  TabBtn,
  TabsBar,
  TabsFadeRight,
  ContentArea,
  SettingsLayout,
} from './settings.styled';
import { useAuth } from '../../../contexts/auth/auth-context';
import { useGlobalNotifications } from '../../../hooks/use-global-notifications';
import { SettingsProvider } from './settings-context';
import { ModalProvider } from './modal-context';
import { ModalManager } from './modal-manager';
import { AppAvatar } from '../../../components/avatar/avatar';

type Props = {
  open: boolean;
  onClose: () => void;
  initialTab?: SettingsTab;
};

function getProfileInitials(profile: any): string {
  const fn = String(profile?.first_name ?? '').trim();
  const ln = String(profile?.last_name ?? '').trim();
  const nick = String(profile?.nickname ?? '').trim();

  const a = fn.charAt(0);
  const b = ln.charAt(0);

  const out = `${a}${b}`.trim().toUpperCase();
  if (out) return out;

  if (nick) return nick.charAt(0).toUpperCase();

  const email = String(profile?.email_address ?? '').trim();
  if (email) return email.charAt(0).toUpperCase();

  return '?';
}

export default function SettingsFullscreenModal({ open, onClose, initialTab }: Props) {
  const {
    tab,
    setTab,
    tabs,
    isHiddenTab,
    variants,
    handleBack,
    handleOpenChange,
    openSanctumUpgradeNow,
    handleExternalOpenSanctumUpgradeHandled,
    goToChangePassword,
    goToComparePlans,
    handlePasswordChanged,
    triggerSanctumUpgrade,
    profileData,
    notificationsData,
    accountData,
    isLoadingPasskeys,
    isLoadingPayment,
    isLoadingBillingDetails,
    handleNotificationsChanged,
  } = useSettingsModal({ onClose, open, initialTab });

  const { showLoading, hideNotification } = useGlobalNotifications();
  const { profile } = useAuth();

  // Show loading notification when tab is active and data is loading
  useEffect(() => {
    // Only show loading notifications when the modal is actually open
    if (!open) {
      return;
    }

    let message = '';

    // Only show loading for async fetched tabs.
    // Profile tab uses live auth context which is already loaded (or handled by App shell).
    if (tab === 'account' && isLoadingPasskeys) {
      message = 'Loading account data...';
    } else if (tab === 'billing' && (isLoadingPayment || isLoadingBillingDetails)) {
      message = 'Loading billing data...';
    }

    if (message) {
      showLoading(message);
    } else {
      hideNotification();
    }

    return () => hideNotification();
  }, [
    open,
    tab,
    isLoadingPasskeys,
    isLoadingPayment,
    isLoadingBillingDetails,
    showLoading,
    hideNotification,
  ]);

  const initials = getProfileInitials(profile);

  return (
    <SettingsProvider open={open}>
      <ModalProvider open={open}>
        <Dialog.Root open={open} onOpenChange={handleOpenChange}>
          <Dialog.Portal>
            <Overlay forceMount />
            <Sheet forceMount role="dialog" aria-label="Settings">
              {/* Header */}
              <SheetHeader>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <img
                    onClick={handleBack}
                    title="Back"
                    data-testid="settings-back"
                    src="/settings-back.svg"
                    alt="Back"
                    width={44}
                    height={44}
                    style={{ display: 'block', cursor: 'pointer' }}
                  />
                  <img
                    src="/main-logo-white.svg"
                    alt="Mailander logo"
                    height={12}
                    style={{ paddingLeft: '8px', display: 'block' }}
                  />
                </div>

                <AppAvatar
                  userId={String(profile?.id ?? '')}
                  version={(profile as any)?.avatar_version ?? (profile as any)?.updated_at ?? null}
                  hasAvatar={!!profile?.avatar_url}
                  alt="You"
                  initials={initials}
                  width={34}
                  height={34}
                  imgStyle={{ borderRadius: '999px', objectFit: 'cover' }}
                  fallbackStyle={{ borderRadius: '999px' }}
                />
              </SheetHeader>

              {/* Title (a11y-friendly) */}
              <Dialog.Title asChild>
                {tab === 'compare-plans' ? <H1>Compare Plans</H1> : <H1>Settings</H1>}
              </Dialog.Title>
              <Dialog.Description
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0,0,0,0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                Update your profile, notifications, account, privacy, and billing preferences.
              </Dialog.Description>

              <SettingsLayout>
              {/* Tabs — hidden on "change-password" and "compare-plans" */}
              {!isHiddenTab && (
                <TabsBar aria-label="Settings sections" role="tablist">
                  <NavTitle>{tab === 'compare-plans' ? 'Compare Plans' : 'Settings'}</NavTitle>
                  {tabs.map(({ key, label }) => {
                    const active = tab === key;
                    return (
                      <TabBtn
                        key={key}
                        data-active={active || undefined}
                        onClick={() => setTab(key)}
                        aria-selected={active}
                        role="tab"
                        id={`${key}-tab`}
                      >
                        {label}
                      </TabBtn>
                    );
                  })}
                  <TabsFadeRight aria-hidden />
                </TabsBar>
              )}

              {/* Content — single scrollable area */}
              <ContentArea role="tabpanel" aria-labelledby={`${isHiddenTab ? 'profile' : tab}-tab`}>
                <>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={tab}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      variants={variants}
                    >
                      {tab === 'profile' && <ProfileSettings />}

                      {tab === 'notifications' && (
                        <NotificationsSettings
                          initialNotificationMethod={notificationsData.notification_method}
                          initialSmsConsent={notificationsData.sms_consent}
                          onSettingsChanged={handleNotificationsChanged}
                        />
                      )}

                      {tab === 'account' && (
                        <AccountSettings
                          onChangePasswordRequested={goToChangePassword}
                          initialPasskeys={accountData.passkeys}
                          initialAutoPromptPasskey={accountData.auto_prompt_passkey}
                        />
                      )}

                      {tab === 'privacy' && <PrivacySettings />}

                      {tab === 'billing' && (
                        <BillingSettings
                          onComparePlansRequested={goToComparePlans}
                          externalOpenSanctumUpgrade={openSanctumUpgradeNow}
                          onExternalOpenSanctumUpgradeHandled={
                            handleExternalOpenSanctumUpgradeHandled
                          }
                          profileEmail={profileData?.email_address}
                        />
                      )}

                      {tab === 'change-password' && (
                        <ChangePasswordPanel
                          handleBack={handleBack}
                          onPasswordChanged={handlePasswordChanged}
                        />
                      )}

                      {tab === 'compare-plans' && (
                        <ComparePlansPanel
                          onGoBack={() => setTab('billing')}
                          onUpgradeToNorthStar={triggerSanctumUpgrade}
                        />
                      )}

                      <div
                        style={{ height: 'env(safe-area-inset-bottom)', paddingBottom: '16px' }}
                      />
                    </motion.div>
                  </AnimatePresence>

                </>
              </ContentArea>
              </SettingsLayout>

              {/* Centralized Modal Manager — overlays the entire Sheet */}
              <ModalManager />
            </Sheet>
          </Dialog.Portal>
        </Dialog.Root>
      </ModalProvider>
    </SettingsProvider>
  );
}
